var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useCallback, useEffect } from 'react';
import { getDVRWindowLocationInProgramWindow, getLiveProgressFromPercentage, getLiveProgressPercentageBasedOnCurrentTime, } from '@24i/player-utils';
import { SeekType } from '@24i/player-base';
import { useAutoUpdate, usePrevious, usePlayerTimes, useAdsData } from '@24i/player-utils-react';
import { usePlayerUIContext, seekingStarted, seekingEnded } from '@24i/player-ui';
import { useTheme } from '@24i/nxg-sdk-higgs/src/hooks/useTheme';
import { Container, AbsoluteContainer, ProgressDot, DVRWindow, getDefaultTimelineStyles, } from './styled';
import { Buffered } from './components/Buffered';
import { SeekingManager } from './components/SeekingManager';
import { ProgressTime } from './components/ProgressTime';
import { Midrolls } from './components/Midrolls';
import { Thumbnail } from './components/Thumbnail';
const PERCENT = 100;
export const Timeline = ({ playerInstance, programRange, className = '', styles = {}, dvrWindowOverride, shouldShowBufferedRanges = true, shouldShowProgressTime = false, showHours = true, variant, }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
    const { state: { relativeSeekValue, isVolumeOpened, isSeeking, isOnLiveEdge }, dispatch, } = usePlayerUIContext();
    const { theme } = useTheme();
    const customStyles = Object.assign(Object.assign({}, getDefaultTimelineStyles(theme)), styles);
    const isLive = !!(playerInstance === null || playerInstance === void 0 ? void 0 : playerInstance.live);
    const tick = useAutoUpdate();
    const { elapsedTime, durationTime } = usePlayerTimes(playerInstance);
    const previousRelativeSeekValue = usePrevious(relativeSeekValue);
    const timelineDomContainer = React.useRef(null);
    const [rawProgress, setProgress] = useState(0);
    const [hoverProgress, setHoverProgress] = useState(0);
    const [isHovering, setIsHovering] = useState(false);
    const [thumbnail, setThumbnail] = useState();
    const [preloadStarted, setPreloadStarted] = useState(false);
    let preloadIteration = 0;
    const { isPlayingClientAd, isSeekingBackwardDisabled, isSeekingForwardDisabled } = useAdsData(playerInstance);
    const isSeekingDisabled = isSeekingBackwardDisabled && isSeekingForwardDisabled;
    const isActive = Boolean(isSeeking || isHovering || relativeSeekValue);
    let start = 0;
    let end = 0;
    const { seekableRange, timeshiftRange, live } = playerInstance !== null && playerInstance !== void 0 ? playerInstance : {};
    if (playerInstance) {
        if (!live) {
            start = seekableRange.start;
            end = seekableRange.end;
        }
        else {
            end = timeshiftRange.end;
            start = dvrWindowOverride
                ? timeshiftRange.end - dvrWindowOverride
                : timeshiftRange.start;
        }
    }
    const { programStart: metadataStart, programEnd: metadataEnd } = (playerInstance === null || playerInstance === void 0 ? void 0 : playerInstance.getMetadata()) || {
        programStart: 0,
        programEnd: 0,
    };
    const rangeStart = (_a = programRange === null || programRange === void 0 ? void 0 : programRange.start) !== null && _a !== void 0 ? _a : metadataStart;
    const rangeEnd = (_b = programRange === null || programRange === void 0 ? void 0 : programRange.end) !== null && _b !== void 0 ? _b : metadataEnd;
    const dvrWindowEndDate = (_d = (timeshiftRange && ((_c = playerInstance === null || playerInstance === void 0 ? void 0 : playerInstance.getUTCTimestamp(timeshiftRange.end)) === null || _c === void 0 ? void 0 : _c.getTime()))) !== null && _d !== void 0 ? _d : Date.now();
    const dvrWindowLocationWithinProgram = getDVRWindowLocationInProgramWindow(isLive, rangeStart, rangeEnd, start, end, dvrWindowEndDate);
    const progress = Math.max(dvrWindowLocationWithinProgram.start, Math.min(dvrWindowLocationWithinProgram.end, rawProgress));
    const seekToTime = isLive
        ? getLiveProgressFromPercentage(rangeStart, rangeEnd, start, end, progress / PERCENT, dvrWindowEndDate)
        : (progress / PERCENT) * durationTime;
    const thumbnailsPreload = (timeEnd) => __awaiter(void 0, void 0, void 0, function* () {
        const maxIterations = 100;
        const thumbnailsArray = yield playerInstance.getThumbnails([timeEnd]);
        const preloadThumbnail = thumbnailsArray ? thumbnailsArray[0] : null;
        preloadIteration++;
        if (preloadThumbnail) {
            const img = new Image();
            img.src = preloadThumbnail.url;
            let nextImageTime = preloadThumbnail.end + 1;
            if (preloadThumbnail.imageHeight && preloadThumbnail.imageWidth) {
                const xQuantity = preloadThumbnail.imageWidth / preloadThumbnail.width;
                const yQuantity = preloadThumbnail.imageHeight / preloadThumbnail.height;
                const totalQty = xQuantity * yQuantity;
                nextImageTime =
                    preloadThumbnail.end +
                        totalQty * (preloadThumbnail.end - preloadThumbnail.start);
            }
            if (preloadThumbnail.end < durationTime && preloadIteration < maxIterations)
                thumbnailsPreload(nextImageTime * 1000);
        }
    });
    useEffect(() => {
        var _a;
        if (isSeeking || (relativeSeekValue === null && previousRelativeSeekValue))
            return;
        if (!isLive && !durationTime)
            return;
        const playerCurrentTime = (_a = playerInstance === null || playerInstance === void 0 ? void 0 : playerInstance.currentTime) !== null && _a !== void 0 ? _a : 0;
        const progressPercentage = isLive
            ? getLiveProgressPercentageBasedOnCurrentTime(rangeStart, rangeEnd, start, end, playerCurrentTime, relativeSeekValue, dvrWindowEndDate)
            : (playerCurrentTime + relativeSeekValue) / durationTime;
        setProgress(parseFloat(Math.max(0, Math.min(100, progressPercentage * PERCENT || 0)).toFixed(3)));
        if (progress && !preloadStarted) {
            thumbnailsPreload(0);
            setPreloadStarted(true);
        }
    }, [relativeSeekValue, elapsedTime, tick]);
    useEffect(() => {
        if (!playerInstance || !playerInstance.currentTime)
            return;
        if (isSeeking || isHovering) {
            (function getThumbnailsByHoverTime() {
                return __awaiter(this, void 0, void 0, function* () {
                    let time = seekToTime;
                    if (isHovering) {
                        time = isLive
                            ? getLiveProgressFromPercentage(rangeStart, rangeEnd, start, end, hoverProgress / PERCENT, dvrWindowEndDate)
                            : (hoverProgress / PERCENT) * durationTime;
                    }
                    try {
                        const thumbnails = yield playerInstance.getThumbnails([time]);
                        if (thumbnails && thumbnails.length)
                            setThumbnail(thumbnails[0]);
                    }
                    catch (e) {
                        console.error('Error trying to get thumbnails: ', e);
                    }
                });
            })();
        }
    }, [isSeeking, isHovering, progress, hoverProgress]);
    const onSeekingStart = useCallback(() => {
        dispatch(seekingStarted());
        setProgress(parseFloat(hoverProgress.toFixed(3)));
    }, [hoverProgress]);
    const onSeekingEnd = useCallback(() => {
        playerInstance === null || playerInstance === void 0 ? void 0 : playerInstance.seek(seekToTime, SeekType.CURRENT_TIME);
        dispatch(seekingEnded());
    }, [playerInstance, seekToTime]);
    const onSeekingProgress = useCallback((seekingProgress) => {
        if (isSeeking)
            setProgress(parseFloat(seekingProgress.toFixed(3)));
        if (isHovering)
            setHoverProgress(parseFloat(seekingProgress.toFixed(3)));
    }, [isSeeking, isHovering]);
    const finalIsOnLiveEdge = (_e = (isLive && isOnLiveEdge)) !== null && _e !== void 0 ? _e : !!(playerInstance === null || playerInstance === void 0 ? void 0 : playerInstance.isOnLiveEdge(seekToTime));
    const progressBarColor = finalIsOnLiveEdge
        ? (_g = (_f = customStyles.progressLive) === null || _f === void 0 ? void 0 : _f.backgroundColor) !== null && _g !== void 0 ? _g : 'transparent'
        : ((_h = customStyles.progressBar) === null || _h === void 0 ? void 0 : _h.backgroundColor) || ((_j = customStyles.progressDot) === null || _j === void 0 ? void 0 : _j.backgroundColor);
    return (React.createElement(Container, { testID: "timeline", style: customStyles.container, ref: timelineDomContainer, className: `${className} Timeline`, onDoubleClick: (event) => {
            event.stopPropagation();
            event.preventDefault();
        }, isActive, isVolumeOpened, variant },
        React.createElement(AbsoluteContainer, { style: customStyles.backgroundContainer, className: "Timeline-Container", "data-test": "Timeline-Container", color: ((_k = theme === null || theme === void 0 ? void 0 : theme.color) === null || _k === void 0 ? void 0 : _k.progressBarBackground) || 'rgba(255, 255, 255, 0.3)', progress: 100 }),
        !!shouldShowBufferedRanges && (React.createElement(Buffered, { style: customStyles.buffered, className: "Timeline-Buffered", "data-test": "Timeline-Buffered", playerInstance, duration: durationTime })),
        isLive && !isPlayingClientAd && (React.createElement(DVRWindow, { style: Object.assign({ backgroundColor: (_l = theme === null || theme === void 0 ? void 0 : theme.color) === null || _l === void 0 ? void 0 : _l.progressBarLive }, customStyles.dvrWindow), className: "Timeline-DVRWindow", "data-test": "Timeline-DVRWindow", dvrWindowFromLeft: dvrWindowLocationWithinProgram.start, dvrWindowFromRight: dvrWindowLocationWithinProgram.end })),
        React.createElement(AbsoluteContainer, { style: Object.assign(Object.assign({}, customStyles === null || customStyles === void 0 ? void 0 : customStyles.progressBar), { backgroundColor: progressBarColor }), className: "Timeline-Progress", "data-test": "Timeline-Progress", dvrWindowFromLeft: dvrWindowLocationWithinProgram.start, dvrWindowFromRight: dvrWindowLocationWithinProgram.end, progress }),
        !isPlayingClientAd && (React.createElement(Midrolls, { style: customStyles.midrolls, className: "Timeline-Midrolls", "data-test": "Timeline-Midrolls", playerInstance })),
        !isPlayingClientAd && (React.createElement(ProgressDot, { style: customStyles.progressDot, className: "Timeline-ProgressDot", "data-test": "Timeline-ProgressDot", dvrWindowFromLeft: dvrWindowLocationWithinProgram.start, dvrWindowFromRight: dvrWindowLocationWithinProgram.end, progress, isActive, isVolumeOpened })),
        React.createElement(React.Fragment, null,
            !!thumbnail && (isSeeking || isHovering) && (React.createElement(Thumbnail, { progress: ((isSeeking || relativeSeekValue) && progress) ||
                    (isHovering && hoverProgress), getTimelineElement: () => timelineDomContainer, isVisible: isActive, playerInstance,
                thumbnail })),
            React.createElement(ProgressTime, { styles: customStyles.progressTime, className: "Timeline-ProgressTime", progress: ((shouldShowProgressTime || isSeeking || relativeSeekValue) && progress) ||
                    (isHovering && hoverProgress), isVisible: isActive || shouldShowProgressTime, duration: durationTime, playerInstance, programRange, showHours })),
        React.createElement(SeekingManager, { parentElement: timelineDomContainer.current, onHoverEnd: () => {
                setIsHovering(false);
            }, onHoverStart: () => {
                setIsHovering(true);
            }, onSeekingStart,
            onSeekingProgress,
            onSeekingEnd,
            isSeekingDisabled })));
};
//# sourceMappingURL=index.js.map